import { useEffect, useRef } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Link2,
  Scissors,
  Sparkles,
  WandSparkles,
} from 'lucide-react'
import { charCount, columnMeta, relativeTime } from '../lib/utils'

function AutoTextarea({ value, onChange, placeholder, className }) {
  const ref = useRef(null)
  useEffect(() => {
    if (!ref.current) return
    ref.current.style.height = 'auto'
    ref.current.style.height = ref.current.scrollHeight + 'px'
  }, [value])
  return (
    <textarea
      ref={ref}
      className={className}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      spellCheck={false}
    />
  )
}

export default function DocumentView({
  project,
  card,
  onChangeCard,
  onSelectCard,
  onOpenRoundtable,
  onOpenFinish,
  onSplit,
}) {
  if (!card) {
    return (
      <div className="document-view dp-empty">
        <div>
          <p>还没有选中一张卡片。</p>
          <button className="btn btn--ink" onClick={() => onSelectCard(project.cards[0]?.id)}>打开第一张卡片</button>
        </div>
      </div>
    )
  }

  const index = project.cards.findIndex((item) => item.id === card.id)
  const prev = project.cards[index - 1]
  const next = project.cards[index + 1]
  const status = columnMeta(card.column)
  const relatedLinks = project.connections.filter((link) => link.source === card.id || link.target === card.id)
  const relatedCards = relatedLinks.map((link) => {
    const otherId = link.source === card.id ? link.target : link.source
    const other = project.cards.find((item) => item.id === otherId)
    return other ? { card: other, link } : null
  }).filter(Boolean)
  const pageNumber = String(index + 1).padStart(2, '0')

  return (
    <div className="document-view">
      <header className="doc-toolbar">
        <div>
          <div className="eyebrow-row">
            <span className="eyebrow-dot" />
            MANUSCRIPT
            <span className="eyebrow-slash">/</span>
            {card.act}
          </div>
          <div className="doc-heading-row">
            <h1>文稿</h1>
            <span className="doc-card-status"><i style={{ background: status.color }} />{status.label}</span>
          </div>
        </div>
        <div className="view-actions">
          <button className="btn btn--outline" onClick={() => onSplit(card.id)}>
            <Scissors size={15} /> 拆分本页
          </button>
          <button className="btn btn--outline" onClick={() => onOpenRoundtable([card.id])}>
            <Sparkles size={15} /> 圆桌讨论
          </button>
          <button className="btn btn--ink" onClick={onOpenFinish}>
            <WandSparkles size={15} /> 收束定稿
          </button>
        </div>
      </header>

      <div className="doc-scroll">
        <div className="paper-stage">
          <div className="paper-folio">PAGE {pageNumber} / {String(project.cards.length).padStart(2, '0')}</div>
          <article className="manuscript-paper">
            <div className="paper-brand">
              <span>{project.title}</span>
              <span>{project.type} · 第一稿</span>
            </div>
            <div className="paper-head">
              <span className="paper-act">{card.act}</span>
              <span className="paper-index">CARD / {pageNumber}</span>
            </div>
            <input
              className="paper-title-input"
              value={card.title}
              onChange={(event) => onChangeCard({ title: event.target.value })}
              placeholder="这一页的名字"
              aria-label="卡片标题"
            />
            <div className="paper-byline">
              <span>页屿手稿</span>
              <span className="byline-dot" />
              <span>{relativeTime(card.updatedAt)}</span>
              <span className="byline-dot" />
              <span>{charCount(card.content)} 字</span>
              <div className="paper-tags">
                {(card.tags || []).map((tag) => <span key={tag}>{tag}</span>)}
              </div>
            </div>

            <div className="paper-body-wrap">
              <div className="paper-body-gutter">
                <span>{pageNumber}</span>
                <i />
              </div>
              <AutoTextarea
                className="paper-body"
                value={card.content}
                onChange={(value) => onChangeCard({ content: value })}
                placeholder="从这里开始写。先写一个动作，或者一句还没有说完的话……"
              />
            </div>

            <div className="paper-foot">
              <button className="page-turn" disabled={!prev} onClick={() => prev && onSelectCard(prev.id)}>
                <ArrowLeft size={14} /> 上一页
              </button>
              <span>· {pageNumber} ·</span>
              <button className="page-turn" disabled={!next} onClick={() => next && onSelectCard(next.id)}>
                下一页 <ArrowRight size={14} />
              </button>
            </div>
          </article>

          {relatedCards.length ? (
            <aside className="paper-relations">
              <div className="paper-relations__label"><Link2 size={13} /> 这张卡片关系着</div>
              {relatedCards.map(({ card: related, link }) => (
                <button key={related.id} onClick={() => onSelectCard(related.id)}>
                  <span>{link.label || '相关'}</span>
                  <strong>{related.title}</strong>
                  <ArrowRight size={12} />
                </button>
              ))}
            </aside>
          ) : null}
        </div>

        <div className="doc-footnote">
          <span><i /> 自动保存到本地</span>
          <span>“{card.summary}”</span>
        </div>
      </div>
    </div>
  )
}
